import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { load } from '@cashfreepayments/cashfree-js';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { useBreakpoint } from '../hooks/useBreakpoint';
import api from '../api/axios';
import { getProductImageUrl } from '../utils/productImage';

const C = { primary: '#8B1A1A', gold: '#D4A017' };
const ACTIVE_PAYMENT_KEY = 'a1looks_active_payment';
const CHECKOUT_REQUEST_KEY = 'a1looks_checkout_request';

const isJutti = (product) => (
  (product.category_slug || product.category?.slug || '').toLowerCase() === 'juttis'
);

const flattenApiErrors = (value) => {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.flatMap(flattenApiErrors);
  if (value && typeof value === 'object') return Object.values(value).flatMap(flattenApiErrors);
  return [];
};

const apiErrorMessage = (error, fallback) => {
  const responseData = error.response?.data;
  if (typeof responseData?.detail === 'string') return responseData.detail;
  if (typeof responseData?.message === 'string') return responseData.message;
  const messages = flattenApiErrors(responseData);
  return messages.length ? messages.join(' ') : (error.message || fallback);
};

const makeClientRequestId = () => (
  window.crypto?.randomUUID?.() ||
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  })
);

const STATES = [
  'Andhra Pradesh', 'Karnataka', 'Kerala', 'Maharashtra', 'Delhi',
  'Gujarat', 'Punjab', 'Rajasthan', 'Tamil Nadu', 'Uttar Pradesh',
  'West Bengal', 'Other',
];

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  border: '1.5px solid #e0d0d0',
  borderRadius: 4,
  fontSize: 14,
  outline: 'none',
  fontFamily: 'Lato, sans-serif',
  boxSizing: 'border-box',
};

export default function Checkout() {
  const { items, subtotal: localSubtotal, shipping: localShipping } = useCart();
  const navigate = useNavigate();
  const cartFingerprint = JSON.stringify(
    items.map(item => [item.product.id, item.quantity, item.size || ''])
  );
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();

  const [form, setForm] = useState({
    full_name: '', email: '', phone: '',
    address: '', city: '', pincode: '', state: '',
  });
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [clientRequestId, setClientRequestId] = useState(() => {
    try {
      const active = JSON.parse(localStorage.getItem(ACTIVE_PAYMENT_KEY) || 'null');
      if (active?.expires_at && new Date(active.expires_at) <= new Date()) {
        localStorage.removeItem(ACTIVE_PAYMENT_KEY);
        localStorage.removeItem(CHECKOUT_REQUEST_KEY);
      }
      const stored = JSON.parse(localStorage.getItem(CHECKOUT_REQUEST_KEY) || 'null');
      if (stored?.cartFingerprint === cartFingerprint && stored?.id) return stored.id;
    } catch {}
    const id = makeClientRequestId();
    localStorage.setItem(
      CHECKOUT_REQUEST_KEY,
      JSON.stringify({ id, cartFingerprint })
    );
    return id;
  });
  const [activePayment, setActivePayment] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(ACTIVE_PAYMENT_KEY) || 'null');
      if (!stored?.expires_at || new Date(stored.expires_at) <= new Date()) {
        localStorage.removeItem(ACTIVE_PAYMENT_KEY);
        return null;
      }
      return stored;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [quoteError, setQuoteError] = useState('');

  const missingSizeItem = useMemo(
    () => items.find(item => isJutti(item.product) && !item.size),
    [items]
  );

  const itemPayload = useMemo(() => items.map(i => ({
    product_id: i.product.id,
    quantity: i.quantity,
    size: i.size || '',
  })), [items]);

  const resetClientRequestId = useCallback(() => {
    const id = makeClientRequestId();
    localStorage.setItem(
      CHECKOUT_REQUEST_KEY,
      JSON.stringify({ id, cartFingerprint })
    );
    setClientRequestId(id);
  }, [cartFingerprint]);

  const refreshQuote = useCallback(async (couponCode = '') => {
    if (!itemPayload.length) {
      setQuote(null);
      return null;
    }
    setQuoteLoading(true);
    try {
      const { data } = await api.post('/payment/quote/', {
        items: itemPayload,
        coupon_code: couponCode,
      });
      setQuote(data);
      setQuoteError('');
      return data;
    } finally {
      setQuoteLoading(false);
    }
  }, [itemPayload]);

  useEffect(() => {
    if (missingSizeItem) {
      setQuote(null);
      setQuoteError(`Select a size for ${missingSizeItem.product.name} before checkout.`);
      return;
    }
    refreshQuote(appliedCoupon?.code || '').catch((err) => {
      setQuote(null);
      setQuoteError(apiErrorMessage(err, 'Unable to calculate the latest order total.'));
    });
  }, [appliedCoupon?.code, missingSizeItem, refreshQuote]);

  const subtotal = quote ? parseFloat(quote.subtotal) : localSubtotal;
  const discount = quote ? parseFloat(quote.discount_amount) : 0;
  const shipping = quote ? parseFloat(quote.shipping) : localShipping;
  const total = quote ? parseFloat(quote.total) : localSubtotal + localShipping;

  const applyCoupon = async () => {
    setCouponError('');
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    try {
      const latestQuote = await refreshQuote(code);
      const coupon = latestQuote?.coupon;
      if (!coupon?.valid) {
        setCouponError('Invalid coupon code.');
        return;
      }
      setAppliedCoupon({ code: coupon.code, label: coupon.label });
      addToast(`Coupon applied: ${coupon.label}`, 'success');
    } catch (err) {
      setCouponError(apiErrorMessage(err, 'Invalid coupon code.'));
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const openCashfreeCheckout = async (payment) => {
    const cashfree = await load({ mode: payment.cashfree_mode });
    const result = await cashfree.checkout({
      paymentSessionId: payment.payment_session_id,
      redirectTarget: '_self',
    });
    if (result?.error) throw new Error(result.error.message || 'Unable to open Cashfree checkout.');
  };

  const resumePayment = async () => {
    if (!activePayment) return;
    setLoading(true);
    setError('');
    try {
      await openCashfreeCheckout(activePayment);
    } catch (err) {
      setError(err.message || 'Unable to resume payment.');
      setLoading(false);
    }
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (items.length === 0) { setError('Your cart is empty.'); return; }
    if (missingSizeItem) {
      setError(`Select a size for ${missingSizeItem.product.name} before checkout.`);
      return;
    }
    if (activePayment) {
      await resumePayment();
      return;
    }
    if (!quote || quoteLoading) {
      setError('Please wait while we confirm the latest order total.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const { data } = await api.post('/payment/create/', {
        ...form,
        client_request_id: clientRequestId,
        coupon_code: appliedCoupon?.code || '',
        items: itemPayload,
      });

      localStorage.setItem(ACTIVE_PAYMENT_KEY, JSON.stringify(data));
      setActivePayment(data);
      await openCashfreeCheckout(data);
    } catch (err) {
      const errorCode = err.response?.data?.code;
      if (errorCode === 'checkout_expired' || errorCode === 'gateway_order_rejected') {
        resetClientRequestId();
        setError('Checkout session refreshed. Please click Pay again.');
      } else {
        setError(apiErrorMessage(err, 'Payment initialisation failed. Please try again.'));
      }
      setLoading(false);
    }
  };

  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6 };
  const fieldStyle = { marginBottom: 16 };

  const OrderSummary = () => (
    <div style={{ background: '#fff', borderRadius: 8, padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, marginBottom: 16 }}>Order Summary</h2>
      {items.map(item => (
        <div key={item.id} style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' }}>
          <img
            src={getProductImageUrl(item.product, 'https://placehold.co/50x60/8B1A1A/gold?text=A1')}
            alt={item.product.name}
            style={{ width: 50, height: 60, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, color: '#2C2C2C', lineHeight: 1.4, marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.product.name}
            </p>
            {item.size && <p style={{ fontSize: 11, color: '#888' }}>Size: {item.size}</p>}
            <p style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>
              ₹{Math.round(parseFloat(item.product.effective_price || item.product.price) * item.quantity).toLocaleString('en-IN')} ×{item.quantity}
            </p>
          </div>
        </div>
      ))}
      <div style={{ borderTop: '1.5px solid #f0e0e0', paddingTop: 14, marginTop: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
          <span style={{ color: '#666' }}>Subtotal</span>
          <span>₹{Math.round(subtotal).toLocaleString('en-IN')}</span>
        </div>
        {discount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6, color: '#2e7d32' }}>
            <span>Discount ({appliedCoupon.code})</span>
            <span>−₹{Math.round(discount).toLocaleString('en-IN')}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 12 }}>
          <span style={{ color: '#666' }}>Shipping</span>
          <span>{shipping === 0 ? 'Free' : `₹${shipping}`}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 17, color: C.primary }}>
          <span>Total</span>
          <span>₹{Math.round(total).toLocaleString('en-IN')}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1050, margin: '0 auto', padding: isMobile ? '24px 16px' : '40px 24px' }}>
      <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 26 : 32, color: '#2C2C2C', marginBottom: 24 }}>
        Checkout
      </h1>

      {activePayment && (
        <div style={{ background: '#fff8e1', border: '1px solid #f0c36d', borderRadius: 6, padding: '14px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <p style={{ fontWeight: 700, fontSize: 14, color: '#6d4c00' }}>You have a pending payment.</p>
            <p style={{ fontSize: 12, color: '#866b24', marginTop: 3 }}>Resume it instead of reserving the same items again.</p>
          </div>
          <button type="button" onClick={resumePayment} disabled={loading} style={{ padding: '9px 16px', background: C.gold, color: '#fff', border: 'none', borderRadius: 4, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
            Resume Payment
          </button>
        </div>
      )}

      {isMobile && (
        <div style={{ marginBottom: 24 }}>
          <OrderSummary />
        </div>
      )}

      <div style={{ display: 'flex', gap: 32, flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
        <form onSubmit={handlePay} style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, marginBottom: 20, color: '#2C2C2C' }}>
            Shipping Details
          </h2>

          <div style={fieldStyle}>
            <label style={labelStyle}>Full Name *</label>
            <input style={inputStyle} name="full_name" value={form.full_name} onChange={handleChange} required placeholder="Your full name" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Email *</label>
              <input style={inputStyle} type="email" name="email" value={form.email} onChange={handleChange} required placeholder="email@example.com" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Phone *</label>
              <input style={inputStyle} type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="+91 XXXXX XXXXX" />
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Address *</label>
            <textarea style={{ ...inputStyle, height: 80, resize: 'vertical' }} name="address" value={form.address} onChange={handleChange} required placeholder="Street address, apartment, etc." />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 16 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>City *</label>
              <input style={inputStyle} name="city" value={form.city} onChange={handleChange} required placeholder="City" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Pincode *</label>
              <input style={inputStyle} name="pincode" value={form.pincode} onChange={handleChange} required placeholder="560001" maxLength={6} />
            </div>
            <div style={{ ...fieldStyle, gridColumn: isMobile ? '1 / -1' : 'auto' }}>
              <label style={labelStyle}>State *</label>
              <select style={{ ...inputStyle, background: '#fff' }} name="state" value={form.state} onChange={handleChange} required>
                <option value="">Select state</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Coupon code */}
          <div style={{ marginBottom: 20, padding: '16px', background: '#FFF8F0', borderRadius: 6, border: '1px solid #f0e0e0' }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 10 }}>Coupon Code</p>
            {appliedCoupon ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#e8f5e9', padding: '10px 14px', borderRadius: 4 }}>
                <span style={{ fontSize: 13, color: '#2e7d32', fontWeight: 600 }}>✓ {appliedCoupon.code} — {appliedCoupon.label}</span>
                <button type="button" onClick={removeCoupon} style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  Remove
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  value={couponInput}
                  onChange={e => { setCouponInput(e.target.value); setCouponError(''); }}
                  style={{ flex: 1, padding: '9px 12px', border: '1.5px solid #e0d0d0', borderRadius: 4, fontSize: 14 }}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), applyCoupon())}
                />
                <button type="button" onClick={applyCoupon} style={{ padding: '9px 16px', background: C.gold, color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Apply
                </button>
              </div>
            )}
            {couponError && <p style={{ color: '#e53935', fontSize: 12, marginTop: 6 }}>{couponError}</p>}
          </div>

          {quoteError && (
            <div style={{ color: '#c62828', fontSize: 14, marginBottom: 16 }}>
              <p>{quoteError}</p>
              {missingSizeItem && (
                <button type="button" onClick={() => navigate('/cart')} style={{ marginTop: 8, padding: 0, background: 'none', border: 'none', color: C.primary, fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
                  Select size in cart
                </button>
              )}
            </div>
          )}
          {error && <p style={{ color: '#e53935', fontSize: 14, marginBottom: 16 }}>{error}</p>}

          {/* Accepted payment methods */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: '#888' }}>Pay via:</span>
            {['UPI', 'Cards', 'Net Banking', 'Wallets'].map(m => (
              <span key={m} style={{ fontSize: 11, padding: '3px 8px', background: '#f5f5f5', borderRadius: 3, color: '#555', border: '1px solid #e0e0e0' }}>{m}</span>
            ))}
          </div>

          <button
            type="submit"
            disabled={loading || quoteLoading || !quote || Boolean(activePayment) || Boolean(missingSizeItem)}
            style={{
              width: '100%',
              padding: '15px',
              background: loading || quoteLoading || !quote || activePayment || missingSizeItem ? '#ccc' : C.primary,
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              fontSize: 16,
              fontWeight: 700,
              cursor: loading || quoteLoading || !quote || activePayment || missingSizeItem ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {loading
              ? 'Redirecting to payment…'
              : quoteLoading
                ? 'Confirming total…'
              : `Pay ₹${total.toLocaleString('en-IN')} Securely`}
          </button>

          <p style={{ textAlign: 'center', fontSize: 12, color: '#aaa', marginTop: 10 }}>
            Secured by Cashfree Payments
          </p>
        </form>

        {!isMobile && (
          <div style={{ width: 300, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 76 }}>
            <OrderSummary />
          </div>
        )}
      </div>
    </div>
  );
}
